/**
 * An image's pixel size from its first bytes — so a huge picture is refused
 * before the browser decodes it into a bitmap (PNG, GIF, WebP and JPEG;
 * anything else reads as unknown and goes on to be decoded as before).
 */

export interface ImageSize {
  width: number;
  height: number;
}

/** A JPEG's size sits in a frame header after its metadata segments, which can run long. */
const HEADER_BYTES = 256 * 1024;

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47];
const PNG_WIDTH_OFFSET = 16;
const PNG_HEIGHT_OFFSET = 20;
const GIF_SIGNATURE = [0x47, 0x49, 0x46, 0x38];
const GIF_WIDTH_OFFSET = 6;
const GIF_HEIGHT_OFFSET = 8;
const RIFF_SIGNATURE = [0x52, 0x49, 0x46, 0x46];
const WEBP_TAG_OFFSET = 8;
const WEBP_CHUNK_OFFSET = 12;
const WEBP_LOSSY_SIZE_OFFSET = 26;
const WEBP_LOSSLESS_SIZE_OFFSET = 21;
const WEBP_EXTENDED_WIDTH_OFFSET = 24;
const WEBP_EXTENDED_HEIGHT_OFFSET = 27;
const WEBP_LOSSY_DIMENSION_MASK = 0x3fff;
const JPEG_SIGNATURE = [0xff, 0xd8];
const JPEG_MARKER_PREFIX = 0xff;
const JPEG_FIRST_SEGMENT_OFFSET = 2;
const JPEG_SEGMENT_HEADER_BYTES = 4;
const JPEG_SIZE_OFFSET_IN_FRAME = 5;
const JPEG_MARKER_DHT = 0xc4;
const JPEG_MARKER_JPG = 0xc8;
const JPEG_MARKER_DAC = 0xcc;
const JPEG_MARKER_SOF_FIRST = 0xc0;
const JPEG_MARKER_SOF_LAST = 0xcf;
const JPEG_MARKER_SOS = 0xda;
const JPEG_MARKER_EOI = 0xd9;
const JPEG_STANDALONE_MARKER_FIRST = 0xd0;
const JPEG_STANDALONE_MARKER_LAST = 0xd8;
const JPEG_MARKER_TEM = 0x01;

function byteAt(bytes: Uint8Array, offset: number): number {
  return bytes[offset] ?? 0;
}

function startsWith(bytes: Uint8Array, signature: number[], offset = 0): boolean {
  return signature.every((byte, index) => byteAt(bytes, offset + index) === byte);
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length));
}

function u16be(bytes: Uint8Array, offset: number): number {
  return (byteAt(bytes, offset) << 8) | byteAt(bytes, offset + 1);
}

function u16le(bytes: Uint8Array, offset: number): number {
  return byteAt(bytes, offset) | (byteAt(bytes, offset + 1) << 8);
}

function u24le(bytes: Uint8Array, offset: number): number {
  return byteAt(bytes, offset) | (byteAt(bytes, offset + 1) << 8) | (byteAt(bytes, offset + 2) << 16);
}

function u32be(bytes: Uint8Array, offset: number): number {
  return ((byteAt(bytes, offset) << 24) | (byteAt(bytes, offset + 1) << 16) | (byteAt(bytes, offset + 2) << 8) | byteAt(bytes, offset + 3)) >>> 0;
}

function u32le(bytes: Uint8Array, offset: number): number {
  return (byteAt(bytes, offset) | (byteAt(bytes, offset + 1) << 8) | (byteAt(bytes, offset + 2) << 16) | (byteAt(bytes, offset + 3) << 24)) >>> 0;
}

function pngSize(bytes: Uint8Array): ImageSize | null {
  if (bytes.length < PNG_HEIGHT_OFFSET + 4) return null;
  return { width: u32be(bytes, PNG_WIDTH_OFFSET), height: u32be(bytes, PNG_HEIGHT_OFFSET) };
}

function gifSize(bytes: Uint8Array): ImageSize | null {
  if (bytes.length < GIF_HEIGHT_OFFSET + 2) return null;
  return { width: u16le(bytes, GIF_WIDTH_OFFSET), height: u16le(bytes, GIF_HEIGHT_OFFSET) };
}

function webpSize(bytes: Uint8Array): ImageSize | null {
  if (ascii(bytes, WEBP_TAG_OFFSET, 4) !== 'WEBP') return null;
  const chunk = ascii(bytes, WEBP_CHUNK_OFFSET, 4);
  if (chunk === 'VP8 ' && bytes.length >= WEBP_LOSSY_SIZE_OFFSET + 4) {
    return {
      width: u16le(bytes, WEBP_LOSSY_SIZE_OFFSET) & WEBP_LOSSY_DIMENSION_MASK,
      height: u16le(bytes, WEBP_LOSSY_SIZE_OFFSET + 2) & WEBP_LOSSY_DIMENSION_MASK,
    };
  }
  if (chunk === 'VP8L' && bytes.length >= WEBP_LOSSLESS_SIZE_OFFSET + 4) {
    const bits = u32le(bytes, WEBP_LOSSLESS_SIZE_OFFSET);
    return { width: (bits & WEBP_LOSSY_DIMENSION_MASK) + 1, height: ((bits >>> 14) & WEBP_LOSSY_DIMENSION_MASK) + 1 };
  }
  if (chunk === 'VP8X' && bytes.length >= WEBP_EXTENDED_HEIGHT_OFFSET + 3) {
    return { width: u24le(bytes, WEBP_EXTENDED_WIDTH_OFFSET) + 1, height: u24le(bytes, WEBP_EXTENDED_HEIGHT_OFFSET) + 1 };
  }
  return null;
}

function isFrameMarker(marker: number): boolean {
  if (marker < JPEG_MARKER_SOF_FIRST || marker > JPEG_MARKER_SOF_LAST) return false;
  return marker !== JPEG_MARKER_DHT && marker !== JPEG_MARKER_JPG && marker !== JPEG_MARKER_DAC;
}

function isStandaloneMarker(marker: number): boolean {
  return marker === JPEG_MARKER_TEM || (marker >= JPEG_STANDALONE_MARKER_FIRST && marker <= JPEG_STANDALONE_MARKER_LAST);
}

function jpegSize(bytes: Uint8Array): ImageSize | null {
  let offset = JPEG_FIRST_SEGMENT_OFFSET;
  while (offset + JPEG_SEGMENT_HEADER_BYTES <= bytes.length) {
    if (byteAt(bytes, offset) !== JPEG_MARKER_PREFIX) return null;
    const marker = byteAt(bytes, offset + 1);
    if (marker === JPEG_MARKER_PREFIX) {
      offset += 1;
      continue;
    }
    if (isStandaloneMarker(marker)) {
      offset += 2;
      continue;
    }
    if (marker === JPEG_MARKER_SOS || marker === JPEG_MARKER_EOI) return null;
    if (isFrameMarker(marker)) {
      if (offset + JPEG_SIZE_OFFSET_IN_FRAME + 4 > bytes.length) return null;
      return { height: u16be(bytes, offset + JPEG_SIZE_OFFSET_IN_FRAME), width: u16be(bytes, offset + JPEG_SIZE_OFFSET_IN_FRAME + 2) };
    }
    offset += 2 + u16be(bytes, offset + 2);
  }
  return null;
}

/** The size in the header of `bytes`; null for a format not read here, or a header cut short. */
export function imageSizeFromHeader(bytes: Uint8Array): ImageSize | null {
  if (startsWith(bytes, PNG_SIGNATURE)) return pngSize(bytes);
  if (startsWith(bytes, GIF_SIGNATURE)) return gifSize(bytes);
  if (startsWith(bytes, RIFF_SIGNATURE)) return webpSize(bytes);
  if (startsWith(bytes, JPEG_SIGNATURE)) return jpegSize(bytes);
  return null;
}

/** Reads only the head of `file`, never the whole of it. Null when it can't be read or isn't understood. */
export async function readImageSize(file: Blob): Promise<ImageSize | null> {
  try {
    const head = await file.slice(0, HEADER_BYTES).arrayBuffer();
    return imageSizeFromHeader(new Uint8Array(head));
  } catch {
    return null;
  }
}

const PNG_FIRST_CHUNK_OFFSET = 8;
const PNG_CHUNK_HEADER_BYTES = 8;
const PNG_CHUNK_CRC_BYTES = 4;
const WEBP_CHUNK_HEADER_BYTES = 8;
const CHUNK_TYPE_BYTES = 4;
const WEBP_ANIMATION_CHUNK = 'ANIM';
/** A still WebP's image data: no animation header can follow it. */
const WEBP_STILL_CHUNKS: ReadonlySet<string> = new Set(['VP8 ', 'VP8L', 'ANMF']);

/** An APNG says so in an `acTL` chunk, which must come before the first `IDAT`. */
function isAnimatedPng(bytes: Uint8Array): boolean {
  let offset = PNG_FIRST_CHUNK_OFFSET;
  while (offset + PNG_CHUNK_HEADER_BYTES <= bytes.length) {
    const type = ascii(bytes, offset + CHUNK_TYPE_BYTES, CHUNK_TYPE_BYTES);
    if (type === 'acTL') return true;
    if (type === 'IDAT' || type === 'IEND') return false;
    offset += PNG_CHUNK_HEADER_BYTES + u32be(bytes, offset) + PNG_CHUNK_CRC_BYTES;
  }
  return false;
}

/** An animated WebP carries an `ANIM` chunk ahead of its frames; chunks are padded to even sizes. */
function isAnimatedWebp(bytes: Uint8Array): boolean {
  if (ascii(bytes, WEBP_TAG_OFFSET, CHUNK_TYPE_BYTES) !== 'WEBP') return false;
  let offset = WEBP_CHUNK_OFFSET;
  while (offset + WEBP_CHUNK_HEADER_BYTES <= bytes.length) {
    const type = ascii(bytes, offset, CHUNK_TYPE_BYTES);
    if (type === WEBP_ANIMATION_CHUNK) return true;
    if (WEBP_STILL_CHUNKS.has(type)) return false;
    const size = u32le(bytes, offset + CHUNK_TYPE_BYTES);
    offset += WEBP_CHUNK_HEADER_BYTES + size + (size % 2);
  }
  return false;
}

/** Whether the header of `bytes` says it is an animated PNG or WebP — which a canvas re-encode would flatten to one frame. */
export function isAnimatedImageHeader(bytes: Uint8Array): boolean {
  if (startsWith(bytes, PNG_SIGNATURE)) return isAnimatedPng(bytes);
  if (startsWith(bytes, RIFF_SIGNATURE)) return isAnimatedWebp(bytes);
  return false;
}

/** Reads only the head of `file`. False when it can't be read or isn't an animated format read here. */
export async function readIsAnimated(file: Blob): Promise<boolean> {
  try {
    const head = await file.slice(0, HEADER_BYTES).arrayBuffer();
    return isAnimatedImageHeader(new Uint8Array(head));
  } catch {
    return false;
  }
}
